import { useEffect } from 'react';
import { useLanguageContext } from '../contexts/LanguageContext';

// Type declaration for Google Translate on window
declare global {
  interface Window {
    google?: {
      translate?: any;
    };
  }
}

export interface UseGoogleTranslateBlockingOptions {
  enableDynamicLanguage?: boolean;
  logActivity?: boolean;
}

export const useGoogleTranslateBlocking = (
  options: UseGoogleTranslateBlockingOptions = {}
) => {
  const { enableDynamicLanguage = true, logActivity = false } = options;
  const { getCurrentLanguageCode, isInitialized } = useLanguageContext();

  useEffect(() => {
    if (!isInitialized) return;

    const enhanceGoogleTranslateBlocking = () => {
      try {
        // 1. Ensure HTML attributes are set
        const htmlElement = document.documentElement;
        if (!htmlElement.getAttribute('translate')) {
          htmlElement.setAttribute('translate', 'no');
          if (logActivity) {
            console.log('🚫 Google Translate: Added translate="no" to HTML element');
          }
        }

        // 2. Ensure body has notranslate class
        if (!document.body.classList.contains('notranslate')) {
          document.body.classList.add('notranslate');
          if (logActivity) {
            console.log('🚫 Google Translate: Added notranslate class to body');
          }
        }

        // 3. Dynamic language attribute based on current language
        if (enableDynamicLanguage) {
          const currentLanguage = getCurrentLanguageCode();
          if (currentLanguage && htmlElement.getAttribute('lang') !== currentLanguage) {
            htmlElement.setAttribute('lang', currentLanguage);
            if (logActivity) {
              console.log(`🌍 Google Translate: Updated lang attribute to: ${currentLanguage}`);
            }
          }
        }

        // 4. Ensure meta tags exist (double-check for dynamic content)
        const existingMetaTag = document.querySelector('meta[name="google"][content="notranslate"]');
        if (!existingMetaTag) {
          const metaTag = document.createElement('meta');
          metaTag.name = 'google';
          metaTag.content = 'notranslate';
          document.head.appendChild(metaTag);
          
          if (logActivity) {
            console.log('🚫 Google Translate: Added meta tag dynamically');
          }
        }

        // 5. Block Google Translate JavaScript if it attempts to load
        if (window.google && window.google.translate) {
          if (logActivity) {
            console.warn('⚠️ Google Translate: Detected Google Translate script, blocking...');
          }
          
          // Disable Google Translate functionality
          try {
            delete window.google.translate;
            if (logActivity) {
              console.log('🚫 Google Translate: Successfully blocked Google Translate script');
            }
          } catch (error) {
            if (logActivity) {
              console.error('❌ Google Translate: Failed to block Google Translate script:', error);
            }
          }
        }

        // 6. Monitor for Google Translate CSS injection
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList') {
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  const element = node as Element;
                  
                  // Check for Google Translate elements
                  if (element.classList?.contains('goog-te-banner-frame') ||
                      element.classList?.contains('goog-te-gadget') ||
                      element.id?.includes('google_translate') ||
                      element.className?.includes('goog-te')) {
                    
                    if (logActivity) {
                      console.log('🚫 Google Translate: Detected Google Translate element, removing...', element);
                    }
                    
                    try {
                      element.remove();
                    } catch (error) {
                      if (logActivity) {
                        console.error('❌ Google Translate: Failed to remove Google Translate element:', error);
                      }
                    }
                  }
                }
              });
            }
          });
        });

        // Start observing
        observer.observe(document.body, {
          childList: true,
          subtree: true
        });

        // Cleanup function
        return () => {
          observer.disconnect();
        };

      } catch (error) {
        if (logActivity) {
          console.error('❌ Google Translate Blocking: Error in enhanceGoogleTranslateBlocking:', error);
        }
      }
    };

    // Run enhancement
    const cleanup = enhanceGoogleTranslateBlocking();

    // Cleanup on unmount
    return cleanup;
  }, [getCurrentLanguageCode, isInitialized, enableDynamicLanguage, logActivity]);

  // Return utility functions
  return {
    forceBlockTranslate: () => {
      // Force block translate for specific elements
      const elements = document.querySelectorAll('[translate]:not([translate="no"])');
      elements.forEach(element => {
        element.setAttribute('translate', 'no');
      });
      
      if (logActivity) {
        console.log(`🚫 Google Translate: Force blocked ${elements.length} elements`);
      }
    },
    
    addNotranslateToElement: (element: HTMLElement) => {
      element.classList.add('notranslate');
      element.setAttribute('translate', 'no');
      
      if (logActivity) {
        console.log('🚫 Google Translate: Added notranslate to element:', element);
      }
    }
  };
};

// Export type for the hook return value
export type UseGoogleTranslateBlockingReturn = ReturnType<typeof useGoogleTranslateBlocking>; 